import { useCssVars as _useCssVars, defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, createVNode as _createVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, withModifiers as _withModifiers, withKeys as _withKeys, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle, toHandlers as _toHandlers, mergeProps as _mergeProps, unref as _unref, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["data-testid"]
const _hoisted_2 = { key: 0 }
const _hoisted_3 = {
  key: 0,
  class: "item-card-pill",
  "data-testid": "item-card-pill"
}
const _hoisted_4 = ["role", "tabindex", "aria-label", "onKeydown"]
const _hoisted_5 = { class: "item-card-header-right" }
const _hoisted_6 = {
  key: 0,
  class: "item-card-header-statuses"
}
const _hoisted_7 = ["data-testid"]
const _hoisted_8 = {
  key: 1,
  class: "item-card-header-action-menu"
}
const _hoisted_9 = {
  key: 1,
  class: "item-card-content",
  "data-testid": "item-card-content"
}

import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from '@shell/composables/useI18n';
import LazyImage from '@shell/components/LazyImage.vue';
import { DropdownOption } from '@components/RcDropdown/types';
import ActionMenu from '@shell/components/ActionMenuShell.vue';
import RcItemCardAction from './RcItemCardAction';

type RcItemCardVariant = 'small' | 'medium';

/**
 * A label that can be either plain text or a translatable key.
 */
type Label = {
  key?: string;
  text?: string;
};

/**
 * Represents an image used in the card.
 */
type Image = {
  src: string;
  alt?: Label;
};

/**
 * Optional pill badge, typically used to highlight a tag or state.
 */
type Pill = {
  label: Label;
  tooltip?: Label;
};

/**
 * Represents an icon-based status indicator shown in the card header.
 */
type Status = {
  icon: string;
  color?: string;
  customColor?: string;
  tooltip?: Label;
  handleClick?: () => void;
};

/**
 * Header metadata for the card.
 */
type Header = {
  title?: Label;
  statuses?: Status[];
};

/**
 * The generic data value passed to the card.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ItemValue = Record<string, any>;

/**
 * Props accepted by the ItemCard component.
 */
interface RcItemCardProps {
  /** Unique identifier for the card (used in test IDs) */
  id: string;

  /** Any object value associated with this card */
  value?: ItemValue;

  /** Card title, status icons and action menu. Image will be included in the header in small variant too */
  header: Header;

  /** Optional image to show in card (position depends on variant). A slot is available for it too #item-card-image */
  image?: Image;

  /** Optional actions that will be displayed inside an action-menu
   *
   * Each action should include an `action` name, which is emitted as a custom event when selected.
   * To respond to the event, you must also register a matching event listener using the `@` syntax.
   *
   * Example:
   * <rc-item-card
   *   :actions="[
   *     {
   *       action: 'focusSearch',
   *       label: t('catalog.charts.search'),
   *       enabled: true
   *     }
   *   ]"
   *   @focusSearch="focusSearch"
   * />
   */
  actions?: DropdownOption[];

  /** Text content inside the card body. A slot is available for it too #item-card-content */
  content?: Label;

  /** Layout variant: 'small' or 'medium' */
  variant?: RcItemCardVariant;

  /** Optional pill shown (only if variant is not 'small'). A slot is available for it too #item-card-pill */
  pill?: Pill;

  /** Makes the card clickable and emits 'card-click' on click/enter/space */
  clickable?: boolean;

  /** The card will have same style as hover clickable with the blue border when selected */
  selected?: boolean;

  role?: 'link' | 'button' | undefined;
  /** Disables the card, preventing clicks and keyboard interaction */
  disabled?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'RcItemCard',
  props: {
    id: {},
    value: {},
    header: {},
    image: {},
    actions: {},
    content: {},
    variant: {},
    pill: {},
    clickable: { type: Boolean },
    selected: { type: Boolean },
    role: {},
    disabled: { type: Boolean }
  },
  emits: ["card-click"],
  setup(__props: any, { emit: __emit }) {

_useCssVars(_ctx => ({
  "c19f081c": (cursorValue.value)
}))

const store = useStore();
const { t } = useI18n(store);

/**
 * Variants available for ItemCard layout
 */
const props = __props;

/**
 * Emits:
 * - 'card-click' when card is clicked or activated via keyboard.
 * - custom events defined in the `actions` prop, but only if the corresponding event listener is explicitly declared on the component.
 */
const emit = __emit;

const actionListeners = computed(() => {
  if (!props.actions) return {};

  const listeners: Record<string, (payload: unknown) => void> = {};

  for (const a of props.actions) {
    if (a.action) {
      listeners[a.action] = (payload: unknown) => emit(a.action, payload);
    }
  }

  return listeners;
});

/**
 * Handles the card click while avoiding nested interactive elements
 * By using RcItemCardAction.vue the 'item-card-action' attribute automatically gets added
 * which then gets used to ignore 'card-click'
 */
function _handleCardClick(e: MouseEvent | KeyboardEvent) {
  if (props.disabled) {
    return;
  }
  const interactiveSelector = '[item-card-action]';

  // Prevent card click if the user clicks on an inner actionable element like repo, category, or tag
  if ((e.target as HTMLElement).closest(interactiveSelector)) {
    return;
  }

  emit('card-click', props.value);
}

/**
 * Utility to resolve localized or plain text labels.
 */
function labelText(label?: Label): string {
  return label?.key ? t(label.key) : label?.text ?? '';
}

/** ---------------- data ------------------ */
const cardEl = ref<HTMLElement | null>(null);

/** ---------------- Computed ------------------ */

const headerTitle = computed(() => labelText(props.header.title));
const imageAlt = computed(() => labelText(props.image?.alt));
const pillLabel = computed(() => labelText(props.pill?.label));
const pillTooltip = computed(() => labelText(props.pill?.tooltip));
const contentText = computed(() => labelText(props.content));
const statusTooltips = computed(() => props.header.statuses?.map((status) => labelText(status.tooltip)) || []);

const cardMeta = computed(() => ({
  ariaLabel:       props.clickable ? t('itemCard.ariaLabel.clickable', { cardTitle: labelText(props.header.title) }) : undefined,
  tabIndex:        props.clickable && !props.disabled ? '0' : undefined,
  role:            props.role ?? (props.clickable && !props.disabled ? 'button' : undefined),
  actionMenuLabel: props.actions && t('itemCard.actionMenu.label', { cardTitle: labelText(props.header.title) }),
}));

const cursorValue = computed(() => props.clickable ? 'pointer' : 'auto');

return (_ctx: any,_cache: any) => {
  const _directive_clean_tooltip = _resolveDirective("clean-tooltip")!

  return (_openBlock(), _createElementBlock("div", {
    ref_key: "cardEl",
    ref: cardEl,
    class: _normalizeClass(["item-card", {
      'clickable': _ctx.clickable && !_ctx.disabled,
      'selected': _ctx.selected,
      'disabled': _ctx.disabled
    }]),
    "data-testid": `item-card-${_ctx.id}`,
    onClick: _handleCardClick
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(['item-card-body', _ctx.variant])
    }, [
      (_ctx.variant !== 'small')
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _renderSlot(_ctx.$slots, "item-card-image", {}, () => [
              (_ctx.image)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    class: _normalizeClass(['item-card-image', _ctx.variant]),
                    "data-testid": "item-card-image"
                  }, [
                    _createVNode(LazyImage, {
                      src: _ctx.image.src,
                      alt: imageAlt.value,
                      style: {'width': '40px', 'height': '40px', 'object-fit': 'contain'}
                    }, null, 8, ["src", "alt"])
                  ], 2))
                : _createCommentVNode("", true)
            ]),
            _cache[0] || (_cache[0] = _createTextVNode()),
            _renderSlot(_ctx.$slots, "item-card-pill", {}, () => [
              (_ctx.pill)
                ? _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_3, [
                    _createTextVNode(_toDisplayString(pillLabel.value), 1)
                  ])), [
                    [_directive_clean_tooltip, pillTooltip.value]
                  ])
                : _createCommentVNode("", true)
            ])
          ]))
        : _createCommentVNode("", true),
      _cache[8] || (_cache[8] = _createTextVNode()),
      _createElementVNode("div", {
        class: _normalizeClass(['item-card-body-details', _ctx.variant])
      }, [
        _createElementVNode("div", {
          class: _normalizeClass(['item-card-header', _ctx.variant])
        }, [
          _createElementVNode("div", {
            class: "item-card-header-left",
            "data-testid": `card-header-left`,
            role: cardMeta.value.role,
            tabindex: cardMeta.value.tabIndex,
            "aria-label": cardMeta.value.ariaLabel,
            onClick: _withModifiers(_handleCardClick, ["self"]),
            onKeydown: [
              _withKeys(_handleCardClick, ["enter"]),
              _withKeys(_withModifiers(_handleCardClick, ["prevent"]), ["space"])
            ]
          }, [
            (_ctx.variant === 'small')
              ? _renderSlot(_ctx.$slots, "item-card-image", { key: 0 }, () => [
                  (_ctx.image)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        class: _normalizeClass(['item-card-image', _ctx.variant]),
                        "data-testid": "item-card-image"
                      }, [
                        _createVNode(LazyImage, {
                          src: _ctx.image.src,
                          alt: imageAlt.value,
                          style: {'width': '24px', 'height': '24px', 'object-fit': 'contain'}
                        }, null, 8, ["src", "alt"])
                      ], 2))
                    : _createCommentVNode("", true)
                ])
              : _createCommentVNode("", true),
            _cache[1] || (_cache[1] = _createTextVNode()),
            _renderSlot(_ctx.$slots, "item-card-header-title", {}, () => [
              (_ctx.header.title)
                ? _withDirectives((_openBlock(), _createElementBlock("h3", {
                    key: 0,
                    class: _normalizeClass(['item-card-header-title', _ctx.variant]),
                    "data-testid": "item-card-header-title"
                  }, [
                    _createTextVNode(_toDisplayString(headerTitle.value), 1)
                  ], 2)), [
                    [_directive_clean_tooltip, headerTitle.value]
                  ])
                : _createCommentVNode("", true)
            ])
          ], 40, _hoisted_4),
          _cache[3] || (_cache[3] = _createTextVNode()),
          _createElementVNode("div", _hoisted_5, [
            (_ctx.header.statuses?.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.header.statuses, (status, i) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: i,
                      class: "item-card-header-statuses-status",
                      "data-testid": "item-card-header-statuses-status"
                    }, [
                      _withDirectives(_createElementVNode("i", {
                        class: _normalizeClass(['icon', status.icon, status.color]),
                        style: _normalizeStyle({color: status.customColor}),
                        "data-testid": `item-card-header-status-${i}`
                      }, null, 14, _hoisted_7), [
                        [_directive_clean_tooltip, statusTooltips.value[i]]
                      ])
                    ]))
                  }), 128))
                ]))
              : _createCommentVNode("", true),
            _cache[2] || (_cache[2] = _createTextVNode()),
            (_ctx.$slots['item-card-actions'])
              ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                  _renderSlot(_ctx.$slots, "item-card-actions")
                ]))
              : (_ctx.actions)
                ? (_openBlock(), _createBlock(_unref(RcItemCardAction), {
                    key: 2,
                    class: "item-card-header-action-menu"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(ActionMenu, _mergeProps({
                        "data-testid": "item-card-header-action-menu",
                        "button-aria-label": cardMeta.value.actionMenuLabel,
                        "custom-actions": _ctx.actions
                      }, _toHandlers(actionListeners.value)), null, 16, ["button-aria-label", "custom-actions"])
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true)
          ])
        ], 2),
        _cache[5] || (_cache[5] = _createTextVNode()),
        _renderSlot(_ctx.$slots, "item-card-sub-header"),
        _cache[6] || (_cache[6] = _createTextVNode()),
        (_ctx.$slots['item-card-content'])
          ? _renderSlot(_ctx.$slots, "item-card-content", { key: 0 }, () => [
              _cache[4] || (_cache[4] = _createElementVNode("div", {
                class: "item-card-content",
                "data-testid": "item-card-content"
              }, null, -1))
            ])
          : (_ctx.content)
            ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                _createElementVNode("p", null, _toDisplayString(contentText.value), 1)
              ]))
            : _createCommentVNode("", true),
        _cache[7] || (_cache[7] = _createTextVNode()),
        _renderSlot(_ctx.$slots, "item-card-footer")
      ], 2)
    ], 2)
  ], 10, _hoisted_1))
}
}

})